import React, { useRef, useState } from 'react';
import { Upload, Sparkles, AlertCircle, FileText, Image as ImageIcon, X, Check } from 'lucide-react';
import { Modal } from '../lib/Modal';
import { api, formatApiError } from '../lib/api';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadPhase({ onPick, error, busy }) {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    return (
        <div>
            <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) onPick(f);
                }}
                onClick={() => inputRef.current?.click()}
                style={{
                    border: `2px dashed ${drag ? 'var(--brand-orange)' : 'var(--border)'}`,
                    borderRadius: 14,
                    padding: '2.5rem 1.5rem',
                    textAlign: 'center',
                    cursor: busy ? 'wait' : 'pointer',
                    background: drag ? 'rgba(242, 101, 34, 0.05)' : 'var(--surface-2)',
                    transition: 'all 0.2s',
                    opacity: busy ? 0.6 : 1,
                }}
                data-testid="menu-import-dropzone"
            >
                <Upload size={36} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 6 }}>
                    Trage fișierul aici sau click pentru a-l alege
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
                    PDF, JPG, PNG sau WEBP · maxim {MAX_BYTES / (1024 * 1024)} MB
                </div>
                <div style={{ display: 'flex', gap: 16, justifyContent: 'center', color: 'var(--text-faint)', fontSize: '0.8rem' }}>
                    <span><FileText size={14} style={{ verticalAlign: -2 }} /> PDF de meniu</span>
                    <span><ImageIcon size={14} style={{ verticalAlign: -2 }} /> Poză cu meniu tipărit</span>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
                    style={{ display: 'none' }}
                    data-testid="menu-import-file"
                />
            </div>
            {busy && (
                <div style={{ marginTop: 16, padding: '1rem', borderRadius: 10, background: 'rgba(45, 212, 191, 0.06)', border: '1px solid rgba(45, 212, 191, 0.25)', textAlign: 'center' }}>
                    <Sparkles size={18} style={{ color: 'var(--neon-teal)', marginRight: 8, verticalAlign: -3 }} />
                    <span style={{ color: 'var(--neon-teal)', fontWeight: 600 }}>Claude analizează meniul...</span>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                        15-30s pentru un PDF curat, până la 2-3 min pentru poze sau PDF-uri scanate cu multe pagini. Nu închide tab-ul.
                    </div>
                </div>
            )}
            {error && (
                <div style={{ marginTop: 16, padding: '0.85rem', borderRadius: 10, background: 'rgba(255, 46, 146, 0.06)', border: '1px solid rgba(255, 46, 146, 0.3)', display: 'flex', gap: 10 }}>
                    <AlertCircle size={18} color="var(--neon-magenta)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ fontSize: '0.88rem', color: 'var(--text)' }}>{error}</div>
                </div>
            )}
        </div>
    );
}

function PreviewPhase({ draft, setDraft, onCommit, onReset, error, busy, toggleAllCategory }) {
    const toggleCategory = (idx) => {
        setDraft((d) => ({
            ...d,
            categories: d.categories.map((c, i) => i === idx ? { ...c, selected: !c.selected } : c),
        }));
    };
    const toggleProduct = (catIdx, prodIdx) => {
        setDraft((d) => ({
            ...d,
            categories: d.categories.map((c, i) => i !== catIdx ? c : {
                ...c,
                products: c.products.map((p, j) => j === prodIdx ? { ...p, selected: !p.selected } : p),
            }),
        }));
    };
    const editCategory = (idx, field, value) => {
        setDraft((d) => ({
            ...d,
            categories: d.categories.map((c, i) => i === idx ? { ...c, [field]: value } : c),
        }));
    };
    const editProduct = (catIdx, prodIdx, field, value) => {
        setDraft((d) => ({
            ...d,
            categories: d.categories.map((c, i) => i !== catIdx ? c : {
                ...c,
                products: c.products.map((p, j) => j === prodIdx ? { ...p, [field]: value } : p),
            }),
        }));
    };

    const totalSelectedProducts = draft.categories.reduce(
        (sum, c) => sum + (c.selected ? c.products.filter((p) => p.selected).length : 0),
        0,
    );
    const totalSelectedCategories = draft.categories.filter(
        (c) => c.selected && c.products.some((p) => p.selected),
    ).length;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                    Bifează ce vrei să imporți. Editează numele, descrierile și prețurile direct în tabel.
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={onReset} disabled={busy} data-testid="menu-import-reset">
                    <X size={14} /> Alege alt fișier
                </button>
            </div>

            {draft.notes && (
                <div style={{ padding: '0.65rem 0.85rem', borderRadius: 8, background: 'rgba(0, 217, 255, 0.06)', border: '1px solid rgba(0, 217, 255, 0.25)', marginBottom: 12, fontSize: '0.85rem', color: 'var(--text)' }}>
                    <strong style={{ color: 'var(--neon-cyan)' }}>Note de la AI:</strong> {draft.notes}
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '62vh', overflowY: 'auto', paddingRight: 6 }}>
                {draft.categories.map((cat, ci) => {
                    const selectedCount = cat.products.filter((p) => p.selected).length;
                    return (
                        <div key={ci} className="panel" style={{ padding: '1rem 1.1rem' }} data-testid={`menu-import-cat-${ci}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: cat.selected ? 14 : 0, paddingBottom: cat.selected ? 12 : 0, borderBottom: cat.selected ? '1px solid var(--border-faint)' : 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={!!cat.selected}
                                    onChange={() => toggleCategory(ci)}
                                    style={{ width: 22, height: 22, cursor: 'pointer', flexShrink: 0 }}
                                    data-testid={`menu-import-cat-toggle-${ci}`}
                                />
                                <input
                                    className="input"
                                    value={cat.emoji || ''}
                                    onChange={(e) => editCategory(ci, 'emoji', e.target.value)}
                                    style={{ width: 64, fontSize: '1.4rem', textAlign: 'center', padding: '0.5rem', flexShrink: 0 }}
                                />
                                <input
                                    className="input"
                                    value={cat.name}
                                    onChange={(e) => editCategory(ci, 'name', e.target.value)}
                                    style={{ fontWeight: 700, fontSize: '1.1rem', flex: 1, padding: '0.55rem 0.75rem' }}
                                    data-testid={`menu-import-cat-name-${ci}`}
                                />
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {selectedCount}/{cat.products.length} produse
                                </span>
                                {cat.selected && cat.products.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => toggleAllCategory(ci, selectedCount < cat.products.length)}
                                        className="btn btn-ghost btn-sm"
                                        style={{ fontSize: '0.78rem', flexShrink: 0 }}
                                        title={selectedCount < cat.products.length ? 'Bifează toate produsele' : 'Debifează toate'}
                                    >
                                        {selectedCount < cat.products.length ? 'Bifează tot' : 'Debifează tot'}
                                    </button>
                                )}
                            </div>
                            {cat.selected && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {cat.products.map((prod, pi) => (
                                        <div key={pi} style={{
                                            display: 'flex', gap: 12, alignItems: 'flex-start',
                                            padding: '0.85rem',
                                            borderRadius: 10,
                                            background: prod.selected ? 'var(--surface-2)' : 'transparent',
                                            border: '1px solid var(--border-faint)',
                                            opacity: prod.selected ? 1 : 0.55,
                                        }} data-testid={`menu-import-prod-${ci}-${pi}`}>
                                            <input
                                                type="checkbox"
                                                checked={!!prod.selected}
                                                onChange={() => toggleProduct(ci, pi)}
                                                style={{ width: 20, height: 20, marginTop: 10, cursor: 'pointer', flexShrink: 0 }}
                                            />
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                    <input
                                                        className="input"
                                                        value={prod.name}
                                                        onChange={(e) => editProduct(ci, pi, 'name', e.target.value)}
                                                        placeholder="Nume produs"
                                                        style={{ fontSize: '1rem', fontWeight: 600, padding: '0.6rem 0.8rem', flex: 1 }}
                                                    />
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                        <input
                                                            className="input"
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={prod.price ?? ''}
                                                            onChange={(e) => editProduct(ci, pi, 'price', e.target.value === '' ? null : parseFloat(e.target.value))}
                                                            placeholder="0.00"
                                                            style={{ fontSize: '1rem', fontWeight: 600, padding: '0.6rem 0.8rem', width: 110, textAlign: 'right', color: 'var(--amber)' }}
                                                        />
                                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>RON</span>
                                                    </div>
                                                </div>
                                                <input
                                                    className="input"
                                                    value={prod.description || ''}
                                                    onChange={(e) => editProduct(ci, pi, 'description', e.target.value)}
                                                    placeholder="Descriere (opțional)"
                                                    style={{ fontSize: '0.92rem', padding: '0.55rem 0.75rem', color: 'var(--text-muted)' }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!draft.categories.length && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        AI-ul nu a putut extrage nicio categorie din fișier. Încearcă un fișier mai clar.
                    </div>
                )}
            </div>

            {error && (
                <div style={{ marginTop: 12, padding: '0.85rem', borderRadius: 10, background: 'rgba(255, 46, 146, 0.06)', border: '1px solid rgba(255, 46, 146, 0.3)', color: 'var(--text)', fontSize: '0.88rem' }}>
                    {error}
                </div>
            )}

            <div style={{ marginTop: 16, padding: '0.85rem 1rem', borderRadius: 10, background: 'var(--surface-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: '0.92rem' }}>
                    Vei importa <strong style={{ color: 'var(--amber)' }}>{totalSelectedProducts}</strong> produse în <strong style={{ color: 'var(--amber)' }}>{totalSelectedCategories}</strong> categorii.
                </div>
                <button
                    className="btn btn-primary"
                    onClick={onCommit}
                    disabled={busy || totalSelectedProducts === 0}
                    data-testid="menu-import-commit"
                >
                    {busy ? <span className="spinner" /> : <Check size={16} />}
                    {' '}Importă în meniu
                </button>
            </div>
        </div>
    );
}

export function MenuImportModal({ open, onClose, onImported, usage, onUsageChange }) {
    const [phase, setPhase] = useState('upload'); // 'upload' | 'preview'
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const toggleAllCategory = (catIdx, selectAll) => {
        setDraft((d) => ({
            ...d,
            categories: d.categories.map((c, i) => i !== catIdx ? c : {
                ...c,
                products: c.products.map((p) => ({ ...p, selected: selectAll })),
            }),
        }));
    };

    const reset = () => {
        setPhase('upload');
        setDraft(null);
        setError('');
        setBusy(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleUpload = async (file) => {
        setError('');
        if (file.size > MAX_BYTES) {
            setError(`Fișier prea mare. Maxim ${MAX_BYTES / (1024 * 1024)} MB.`);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/r/menu/import/draft', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 270000,  // 4.5 min — generous buffer over backend's 4-min AI timeout
            });
            const augmented = {
                ...data.draft,
                categories: (data.draft.categories || []).map((c) => ({
                    ...c,
                    selected: true,
                    products: (c.products || []).map((p) => ({ ...p, selected: true })),
                })),
            };
            setDraft(augmented);
            setPhase('preview');
            // Quota was consumed by the draft endpoint — propagate the fresh
            // numbers up so the badge on the trigger button updates without a
            // full auth refresh.
            if (data.usage) onUsageChange?.(data.usage);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const handleCommit = async () => {
        if (!draft) return;
        const payload = {
            categories: draft.categories
                .filter((c) => c.selected)
                .map((c) => ({
                    name: c.name?.trim(),
                    emoji: c.emoji?.trim() || null,
                    products: c.products
                        .filter((p) => p.selected && p.name?.trim())
                        .map((p) => ({
                            name: p.name.trim(),
                            description: (p.description || '').trim() || null,
                            price: p.price ?? 0,
                        })),
                }))
                .filter((c) => c.products.length > 0),
        };
        if (!payload.categories.length) {
            setError('Selectează cel puțin un produs pentru a importa.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const { data } = await api.post('/r/menu/import/commit', payload);
            onImported?.(data);
            handleClose();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const title = phase === 'preview' ? 'Previzualizare meniu import' : 'Importă meniu (AI)';
    // Wider modal on preview so the editable rows breathe; narrow on upload.
    const maxWidth = phase === 'preview' ? 1100 : 600;

    return (
        <Modal open={open} title={title} onClose={handleClose} maxWidth={maxWidth}>
            {usage && usage.quota_monthly != null && (
                <div style={{ marginBottom: 14, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    Importuri folosite luna asta: <strong>{usage.used_this_month}/{usage.quota_monthly}</strong>
                </div>
            )}
            {phase === 'upload' && <UploadPhase onPick={handleUpload} error={error} busy={busy} />}
            {phase === 'preview' && draft && (
                <PreviewPhase
                    draft={draft}
                    setDraft={setDraft}
                    onCommit={handleCommit}
                    onReset={reset}
                    error={error}
                    busy={busy}
                    toggleAllCategory={toggleAllCategory}
                />
            )}
        </Modal>
    );
}
